/**
 * Build your character from ready-made parts: pick a head, a body, and colours.
 *
 * Every head and every body here is finished artwork — hair, beard and face drawn to go together,
 * clothes drawn to go with any head — so whatever is picked looks right, and there is nothing to
 * fiddle into shape. The same drawings as the room: the preview is exactly what everyone will see.
 */

import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Lock, Shuffle } from 'lucide-react'
import { useI18n } from '../lib/i18n'
import { api } from '../lib/api'
import { Chair, Desk, DeveloperFigure, Head, Portrait, lookFor, type Pose } from '../bughunt/Figures'
import {
  COMPANIONS, DEFAULT_AVATAR, EVERYDAY_OUTFITS, EXTRAS, HAIR_COLORS, HATS, HEADPHONES, HEADS, HELD_ITEMS, LAPTOPS, OUTFITS, PREMIUM_ITEMS, SHIRT_COLORS, SKIN_TONES,
  type CompanionKind, type ExtraKind, type HatKind, type HeadphoneModel, type HeldKind,
} from '../bughunt/wardrobe'
import { HeldLaptop } from '../bughunt/Gear'
import { Companion } from '../bughunt/ProItems'
import { tryingPro } from '../bughunt/wardrobe'
import { Segmented } from './primitives'
import type { Avatar } from '../lib/types'


const GENDERS = ['unspecified', 'female', 'male'] as const
const POSES: Pose[] = ['working', 'hunting', 'resting']

const any = <T,>(list: readonly T[]): T => list[Math.floor(Math.random() * list.length)]!

export function CharacterBuilder({
  developerId,
  initial,
  busy,
  onSave,
  onCancel,
}: {
  developerId: string
  initial: Avatar | null
  busy: boolean
  onSave: (avatar: Avatar) => void
  onCancel?: () => void
}) {
  const { t } = useI18n()
  const [avatar, setAvatar] = useState<Avatar>(() => ({ ...DEFAULT_AVATAR, ...(initial ?? {}) }))
  const [pose, setPose] = useState<Pose>('hunting')
  // The Pro items (docs/refactor/47): tried on freely in the preview; saved on a plan that has them.
  const { data: plan } = useQuery({ queryKey: ['billing'], queryFn: api.billing.state, staleTime: 60_000, retry: false })
  const allowed = plan ? plan.entitlements.premiumLooks === true : true
  const trying = tryingPro(avatar, initial, allowed)
  const [askedToSave, setAskedToSave] = useState(false)
  const locked = (value: string) => !allowed && PREMIUM_ITEMS.has(value)
  const proBadge = (value: string) => (locked(value) ? <span className="builder__pro tiny"><Lock size={9} aria-hidden /> {t('character.pro')}</span> : null)
  const save = () => {
    if (trying.length > 0) {
      setAskedToSave(true)
      return
    }
    onSave(avatar)
  }
  const takeOffPro = () => {
    const back: Record<string, string> = {}
    for (const slot of trying) back[slot] = initial?.[slot] ?? DEFAULT_AVATAR[slot] ?? ''
    update(back as Partial<Avatar>)
    setAskedToSave(false)
  }

  const update = (patch: Partial<Avatar>) => setAvatar((current) => ({ ...current, ...patch }))
  const look = useMemo(() => lookFor(developerId, avatar), [developerId, avatar])

  // A model also answers the older on/off question, for anything that still asks it.
  const headphones = (model: HeadphoneModel): Partial<Avatar> => ({ headphones: model, headset: model !== 'none' })

  const surprise = () => {
    const head = any(HEADS)
    update({
      hair: head.hair,
      facialHair: head.facialHair,
      hairColor: any(HAIR_COLORS.slice(0, 9)),
      skinTone: any(Object.keys(SKIN_TONES)),
      outfit: any(EVERYDAY_OUTFITS),
      shirtColor: any(SHIRT_COLORS),
      glasses: Math.random() < 0.35,
      ...headphones(Math.random() < 0.4 ? any(HEADPHONES.slice(1)) : 'none'),
      laptop: any(LAPTOPS),
    })
  }

  return (
    <div className="builder">
      {/* The same drawing as the room, on the same night floor, so what is picked is what is seen. */}
      <div className="builder__preview bh-palette">
        <svg viewBox="-70 -165 140 175" className="builder__stage" aria-hidden="true">
          <defs>
            <radialGradient id="builder-glow" cx="50%" cy="100%" r="80%">
              <stop offset="0%" stopColor="#2A3B63" />
              <stop offset="100%" stopColor="#0B1324" />
            </radialGradient>
          </defs>
          <rect x="-70" y="-165" width="140" height="175" fill="url(#builder-glow)" />
          <ellipse cx="0" cy="2" rx="52" ry="9" fill="rgba(255,255,255,0.06)" />
          {pose === 'resting' ? (
            // At the desk, as the room draws it.
            <g transform="translate(0 -6) scale(0.92)">
              <Chair />
              <DeveloperFigure look={look} pose="resting" swinging={false} />
              <Desk active laptop={look.laptop} />
            </g>
          ) : (
            <DeveloperFigure look={look} pose={pose} swinging={false} />
          )}
        </svg>
        <Segmented<Pose>
          value={pose}
          options={POSES.map((p) => ({ value: p, label: t(`character.pose.${p}`) }))}
          onChange={setPose}
        />
        <button type="button" className="btn btn--sm" onClick={surprise}>
          <Shuffle size={13} aria-hidden /> {t('character.surprise')}
        </button>
      </div>

      <div className="builder__parts">
        <section className="builder__section">
          <h3 className="builder__title">{t('character.head')}</h3>
          <div className="builder__tiles builder__tiles--heads" role="radiogroup" aria-label={t('character.head')}>
            {HEADS.map((head) => {
              const chosen = avatar.hair === head.hair && avatar.facialHair === head.facialHair
              return (
                <button
                  key={`${head.hair}-${head.facialHair}`}
                  type="button"
                  role="radio"
                  aria-checked={chosen}
                  className={`builder__tile${chosen ? ' builder__tile--on' : ''}`}
                  title={`${t(`avatar.hair.${head.hair}`)} · ${t(`avatar.facial.${head.facialHair}`)}`}
                  onClick={() => update({ hair: head.hair, facialHair: head.facialHair })}
                >
                  <Portrait look={{ ...look, hairStyle: head.hair, facialHair: head.facialHair }} size={54} />
                </button>
              )
            })}
          </div>
        </section>

        <Swatches title={t('character.hairColor')} colors={HAIR_COLORS} value={avatar.hairColor} onChange={(hairColor) => update({ hairColor })} />

        <Swatches
          title={t('character.skin')}
          colors={Object.values(SKIN_TONES)}
          value={SKIN_TONES[avatar.skinTone] ?? avatar.skinTone}
          labels={Object.keys(SKIN_TONES).map((key) => t(`avatar.skin.${key}`))}
          onChange={(color) => update({ skinTone: Object.keys(SKIN_TONES).find((key) => SKIN_TONES[key] === color) ?? 'medium' })}
        />

        <section className="builder__section">
          <h3 className="builder__title">{t('character.outfit')}</h3>
          <div className="builder__tiles" role="radiogroup" aria-label={t('character.outfit')}>
            {OUTFITS.map((outfit) => {
              const chosen = (avatar.outfit ?? 'hoodie') === outfit
              return (
                <button
                  key={outfit}
                  type="button"
                  role="radio"
                  aria-checked={chosen}
                  className={`builder__tile builder__tile--outfit${chosen ? ' builder__tile--on' : ''}`}
                  onClick={() => update({ outfit })}
                >
                  <svg viewBox="-31 -104 62 94" width="58" height="88" aria-hidden="true" className="bh-palette">
                    <DeveloperFigure look={{ ...look, outfit }} pose="resting" swinging={false} />
                  </svg>
                  <span className="tiny">{t(`character.outfit.${outfit}`)}</span>
                  {proBadge(outfit)}
                </button>
              )
            })}
          </div>
        </section>

        <Swatches title={t('character.outfitColor')} colors={SHIRT_COLORS} value={avatar.shirtColor} onChange={(shirtColor) => update({ shirtColor })} />

        <section className="builder__section">
          <h3 className="builder__title">{t('character.headphones')}</h3>
          <div className="builder__tiles" role="radiogroup" aria-label={t('character.headphones')}>
            {HEADPHONES.map((model) => {
              const chosen = look.headphones === model
              return (
                <button
                  key={model}
                  type="button"
                  role="radio"
                  aria-checked={chosen}
                  className={`builder__tile builder__tile--outfit${chosen ? ' builder__tile--on' : ''}`}
                  onClick={() => update(headphones(model))}
                >
                  <svg viewBox="-38 -112 76 70" width="66" height="61" aria-hidden="true" className="bh-palette">
                    <DeveloperFigure look={{ ...look, headphones: model }} pose="resting" swinging={false} />
                  </svg>
                  <span className="tiny">{t(`character.headphones.${model}`)}</span>
                </button>
              )
            })}
          </div>
        </section>

        <section className="builder__section">
          <h3 className="builder__title">{t('character.laptop')}</h3>
          <span className="tiny muted">{t('character.laptopHint')}</span>
          <div className="builder__tiles" role="radiogroup" aria-label={t('character.laptop')}>
            {LAPTOPS.map((model) => {
              const chosen = look.laptop === model
              return (
                <button
                  key={model}
                  type="button"
                  role="radio"
                  aria-checked={chosen}
                  className={`builder__tile builder__tile--outfit${chosen ? ' builder__tile--on' : ''}`}
                  onClick={() => {
                    update({ laptop: model })
                    // Show it where it is seen: in their hands while their agent works.
                    setPose('working')
                  }}
                >
                  <svg viewBox="-23 -71 46 34" width="66" height="49" aria-hidden="true" className="bh-palette">
                    <HeldLaptop model={model} />
                  </svg>
                  <span className="tiny">{t(`character.laptop.${model}`)}</span>
                </button>
              )
            })}
          </div>
        </section>

        <section className="builder__section">
          <h3 className="builder__title">{t('character.hat')}</h3>
          <div className="builder__tiles" role="radiogroup" aria-label={t('character.hat')}>
            {HATS.map((hat) => {
              const chosen = look.hat === hat
              return (
                <button key={hat} type="button" role="radio" aria-checked={chosen} className={`builder__tile builder__tile--outfit${chosen ? ' builder__tile--on' : ''}`} onClick={() => update({ hat })}>
                  <svg viewBox="-40 -172 80 86" width="62" height="66" aria-hidden="true" className="bh-palette">
                    <Head look={{ ...look, hat: hat as HatKind }} expression="calm" />
                  </svg>
                  <span className="tiny">{t(`character.hat.${hat}`)}</span>
                  {proBadge(hat)}
                </button>
              )
            })}
          </div>
        </section>

        <section className="builder__section">
          <h3 className="builder__title">{t('character.extra')}</h3>
          <div className="builder__tiles" role="radiogroup" aria-label={t('character.extra')}>
            {EXTRAS.map((extra) => {
              const chosen = look.extra === extra
              return (
                <button key={extra} type="button" role="radio" aria-checked={chosen} className={`builder__tile builder__tile--outfit${chosen ? ' builder__tile--on' : ''}`} onClick={() => update({ extra })}>
                  <svg viewBox="-32 -128 64 60" width="62" height="58" aria-hidden="true" className="bh-palette">
                    <Head look={{ ...look, extra: extra as ExtraKind, hat: 'none' }} expression="calm" />
                  </svg>
                  <span className="tiny">{t(`character.extra.${extra}`)}</span>
                </button>
              )
            })}
          </div>
        </section>

        <section className="builder__section">
          <h3 className="builder__title">{t('character.held')}</h3>
          <span className="tiny muted">{t('character.heldHint')}</span>
          <div className="builder__tiles" role="radiogroup" aria-label={t('character.held')}>
            {HELD_ITEMS.map((held) => {
              const chosen = look.held === held
              return (
                <button
                  key={held}
                  type="button"
                  role="radio"
                  aria-checked={chosen}
                  className={`builder__tile builder__tile--outfit${chosen ? ' builder__tile--on' : ''}`}
                  onClick={() => {
                    update({ held })
                    // Shown where it is seen: in the hunting pose.
                    setPose('hunting')
                  }}
                >
                  <svg viewBox="-42 -178 100 186" width="54" height="100" aria-hidden="true" className="bh-palette">
                    <DeveloperFigure look={{ ...look, held: held as HeldKind, companion: 'none' }} pose="hunting" swinging={false} />
                  </svg>
                  <span className="tiny">{t(`character.held.${held}`)}</span>
                  {proBadge(held)}
                </button>
              )
            })}
          </div>
        </section>

        <section className="builder__section">
          <h3 className="builder__title">{t('character.companion')}</h3>
          <div className="builder__tiles" role="radiogroup" aria-label={t('character.companion')}>
            {COMPANIONS.map((companion) => {
              const chosen = look.companion === companion
              return (
                <button key={companion} type="button" role="radio" aria-checked={chosen} className={`builder__tile builder__tile--outfit${chosen ? ' builder__tile--on' : ''}`} onClick={() => update({ companion })}>
                  <svg viewBox="-64 -134 40 40" width="52" height="52" aria-hidden="true" className="bh-palette">
                    <Companion kind={companion as CompanionKind} />
                  </svg>
                  <span className="tiny">{t(`character.companion.${companion}`)}</span>
                  {proBadge(companion)}
                </button>
              )
            })}
          </div>
        </section>

        <section className="builder__section">
          <h3 className="builder__title">{t('character.details')}</h3>
          <div className="row row--wrap" style={{ gap: 'var(--s3)' }}>
            <Segmented<(typeof GENDERS)[number]>
              value={avatar.gender ?? 'unspecified'}
              options={GENDERS.map((g) => ({ value: g, label: t(`character.traits.${g}`) }))}
              onChange={(gender) => update({ gender })}
            />
            <label className="checkbox">
              <input type="checkbox" checked={avatar.glasses} onChange={(e) => update({ glasses: e.target.checked })} />
              <span className="checkbox__text small">{t('avatar.glasses')}</span>
            </label>
          </div>
          <span className="tiny muted">{t('character.traitsHint')}</span>
        </section>

        {askedToSave && trying.length > 0 && (
          <div className="notice notice--warn small" role="status">
            {t('character.proTrying')}{' '}
            <Link to="/settings/billing">{t('character.proPlans')}</Link>
            <div className="row row--tight" style={{ marginTop: 6 }}>
              <button type="button" className="btn btn--sm" onClick={takeOffPro}>{t('character.proRemove')}</button>
            </div>
          </div>
        )}
        <div className="row">
          <button type="button" className="btn btn--primary" onClick={save} disabled={busy}>
            {busy ? t('common.saving') : t('common.save')}
          </button>
          {onCancel && <button type="button" className="btn" onClick={onCancel}>{t('common.cancel')}</button>}
        </div>
      </div>
    </div>
  )
}

function Swatches({
  title,
  colors,
  value,
  labels,
  onChange,
}: {
  title: string
  colors: string[]
  value: string
  labels?: string[]
  onChange: (color: string) => void
}) {
  return (
    <section className="builder__section">
      <h3 className="builder__title">{title}</h3>
      <div className="builder__swatches" role="radiogroup" aria-label={title}>
        {colors.map((color, i) => {
          const chosen = color.toLowerCase() === value?.toLowerCase()
          return (
            <button
              key={color}
              type="button"
              role="radio"
              aria-checked={chosen}
              aria-label={labels?.[i] ?? color}
              title={labels?.[i] ?? color}
              className={`builder__swatch${chosen ? ' builder__swatch--on' : ''}`}
              style={{ background: color }}
              onClick={() => onChange(color)}
            />
          )
        })}
      </div>
    </section>
  )
}
